import { Link } from "react-router-dom";
import { useAdminProducts } from "@/hooks/useAdminStore";
import { useArticleCatalog } from "@/hooks/useArticleCatalog";
import AdminHeader from "@/pages/admin/components/AdminHeader";

const MARKETPLACE_COLORS: Record<string, string> = {
  Amazon: "bg-amber-100 text-amber-700",
  "Mercado Livre": "bg-yellow-100 text-yellow-700",
  Shopee: "bg-orange-100 text-orange-700",
  "Magazine Luiza": "bg-blue-100 text-blue-700",
  Americanas: "bg-red-100 text-red-700",
  Kabum: "bg-purple-100 text-purple-700",
};

export default function AdminDashboard() {
  const { products } = useAdminProducts();
  const { articles } = useArticleCatalog();

  const activeDeals = products.filter((product) => product.showInDailyDeals).length;
  const featuredProducts = products.filter((product) => product.showInFeatured).length;
  const marketplaceCounts = products.reduce<Record<string, number>>((acc, p) => {
    acc[p.marketplace] = (acc[p.marketplace] || 0) + 1;
    return acc;
  }, {});

  const stats = [
    { icon: "ri-shopping-bag-3-line", label: "Produtos", value: products.length, color: "bg-orange-50 text-orange-600", link: "/admin/produtos" },
    { icon: "ri-fire-line", label: "Ofertas Ativas", value: activeDeals, color: "bg-red-50 text-red-600", link: "/admin/ofertas" },
    { icon: "ri-star-line", label: "Destaques", value: featuredProducts, color: "bg-amber-50 text-amber-600", link: "/admin/destaques" },
    { icon: "ri-article-line", label: "Artigos", value: articles.length, color: "bg-emerald-50 text-emerald-600", link: "/admin/artigos" },
  ];

  const recentProducts = [...products].slice(-5).reverse();
  const recentArticles = [...articles].slice(-4).reverse();

  return (
    <div>
      <AdminHeader
        title="Dashboard"
        subtitle="Visão geral do seu painel de conteúdo"
      />

      <div className="grid grid-cols-4 gap-5 mb-8">
        {stats.map((s) => (
          <Link
            key={s.label}
            to={s.link}
            className="bg-white rounded-2xl p-5 border border-gray-100 hover:border-orange-200 transition-all cursor-pointer group"
          >
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${s.color} mb-3 group-hover:scale-110 transition-transform`}>
              <i className={`${s.icon} text-xl`}></i>
            </div>
            <p className="text-3xl font-bold text-gray-900">{s.value}</p>
            <p className="text-sm text-gray-500 mt-0.5">{s.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-900">Produtos Recentes</h2>
            <Link to="/admin/produtos" className="text-xs text-orange-500 font-semibold hover:underline cursor-pointer">
              Ver todos
            </Link>
          </div>
          <div className="space-y-3">
            {recentProducts.map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0 bg-gray-100"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <p className="text-xs text-gray-400">{p.category} · {p.marketplace}</p>
                    {p.showInDailyDeals && <span className="text-[11px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Oferta</span>}
                    {p.showInFeatured && <span className="text-[11px] font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">Destaque</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-gray-900">R$ {p.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
                  {p.discount > 0 && (
                    <span className="text-xs font-semibold text-red-500">-{p.discount}%</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h2 className="font-bold text-gray-900 mb-4">Por Marketplace</h2>
            <div className="space-y-2.5">
              {Object.entries(marketplaceCounts).map(([mp, count]) => (
                <div key={mp} className="flex items-center justify-between">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${MARKETPLACE_COLORS[mp] || "bg-gray-100 text-gray-600"}`}>
                    {mp}
                  </span>
                  <span className="text-sm font-bold text-gray-700">{count} produto{count !== 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-900">Artigos Recentes</h2>
              <Link to="/admin/artigos" className="text-xs text-orange-500 font-semibold hover:underline cursor-pointer">
                Ver todos
              </Link>
            </div>
            <div className="space-y-3">
              {recentArticles.map((a) => (
                <div key={a.id} className="flex items-start gap-2.5">
                  <img
                    src={a.image}
                    alt={a.title}
                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0 bg-gray-100"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-snug">{a.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{a.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 flex items-center justify-between">
        <div>
          <h3 className="text-white font-bold text-lg">Pronto para adicionar conteúdo?</h3>
          <p className="text-orange-100 text-sm mt-0.5">Cadastre produtos e defina no próprio formulário se eles entram em ofertas e destaques.</p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/admin/produtos"
            className="px-4 py-2.5 bg-white text-orange-600 rounded-xl text-sm font-bold hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            + Produto
          </Link>
          <Link
            to="/admin/ofertas"
            className="px-4 py-2.5 bg-white/20 text-white rounded-xl text-sm font-bold hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
          >
            Gerir Ofertas
          </Link>
          <Link
            to="/admin/destaques"
            className="px-4 py-2.5 bg-white/20 text-white rounded-xl text-sm font-bold hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
          >
            Gerir Destaques
          </Link>
        </div>
      </div>
    </div>
  );
}
